import { AloftArea, AloftPrice } from '@aloft/shared-objects';

import { selectGarageCarportCost } from '../../../redux/features/selectors/costSelectors/selectGarageCarportCost.ts';
import {
  selectDepreciation,
  selectExternalDepreciation,
  selectFunctionalDepreciation,
  selectPhysicalDepreciation,
} from '../../../redux/features/selectors/depreciationSelectors.ts';
import {
  selectImprovementsBasement,
  selectImprovementsGarageCarport,
  selectImprovementsGla,
} from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import {
  selectAdditionalStructureCostNew,
  selectCostNew,
} from '../../../redux/features/selectors/selectCostNew.ts';
import { selectDepreciatedCostOfImprovements } from '../../../redux/features/selectors/selectDepreciatedCostOfImprovements.ts';
import { selectGlaAndBathCostNew } from '../../../redux/features/selectors/selectGlaAndBathCostNew.ts';
import {
  selectSubjectArvValue,
  selectSubjectValue,
} from '../../../redux/features/selectors/selectSubjectValue.ts';
import { selectTotalBasementCost } from '../../../redux/features/selectors/selectTotalBasementCost.ts';
import { RootState } from '../../../redux/types.ts';
import { CostApproachTableData } from '../types.ts';

/**
 * Helper function to select data for subject or ARV subject
 */
type SelectorFn = (state: RootState, key: string) => AloftPrice | AloftArea | undefined | null;

/**
 * Build cost approach data for a single subject (regular or ARV)
 */
const buildSubjectCostData = (
  selectData: (selector: SelectorFn) => AloftPrice | AloftArea | undefined | null,
  valueSelector: SelectorFn,
  exportData: {
    effectiveDateYear?: number;
    quality?: number;
    remainingEconomicLife?: number;
  },
  opinionOfSiteValue: AloftPrice | undefined,
) => ({
  opinionOfSiteValue,
  dwelling: {
    area: selectData(selectImprovementsGla) as AloftArea | undefined,
    cost: selectData(selectGlaAndBathCostNew) as AloftPrice | undefined,
  },
  basement: {
    area: selectData(selectImprovementsBasement) as AloftArea | undefined,
    cost: selectData(selectTotalBasementCost) as AloftPrice | undefined,
  },
  additionalStructures: selectData(selectAdditionalStructureCostNew) as
    | AloftPrice
    | undefined
    | null,
  garageCarport: {
    area: selectData(selectImprovementsGarageCarport) as AloftArea | undefined,
    cost: selectData(selectGarageCarportCost) as AloftPrice | undefined,
  },
  totalEstimateCostNew: selectData(selectCostNew) as AloftPrice | undefined,
  depreciation: {
    physical: selectData(selectPhysicalDepreciation) as AloftPrice | undefined,
    functional: selectData(selectFunctionalDepreciation) as AloftPrice | undefined,
    external: selectData(selectExternalDepreciation) as AloftPrice | undefined,
    total: selectData(selectDepreciation) as AloftPrice | undefined,
  },
  depreciatedCostOfImprovement: selectData(selectDepreciatedCostOfImprovements) as
    | AloftPrice
    | undefined,
  indicatedValueByCostApproach: selectData(valueSelector) as AloftPrice | undefined,
  effectiveDate: exportData.effectiveDateYear,
  qualityRating: exportData.quality,
  remainingEconomicLife: exportData.remainingEconomicLife,
});

/**
 * Build cost approach data for PDF and API export
 */
export const buildCostApproachData = (
  selectSubjectData: (selector: SelectorFn) => AloftPrice | AloftArea | undefined | null,
  selectArvSubjectData: (selector: SelectorFn) => AloftPrice | AloftArea | undefined | null,
  costApproachExportData: {
    effectiveDateYear?: number;
    quality?: number;
    remainingEconomicLife?: number;
  },
  arvCostApproachExportData: {
    effectiveDateYear?: number;
    quality?: number;
    remainingEconomicLife?: number;
  },
  opinionOfSiteValue: AloftPrice | undefined,
  hasArvOption: boolean,
): CostApproachTableData => {
  const costApproachData: CostApproachTableData = {
    subject: buildSubjectCostData(
      selectSubjectData,
      selectSubjectValue,
      costApproachExportData,
      opinionOfSiteValue,
    ),
  };

  if (hasArvOption) {
    costApproachData.subjectArv = buildSubjectCostData(
      selectArvSubjectData,
      selectSubjectArvValue,
      arvCostApproachExportData,
      opinionOfSiteValue,
    );
  }

  return costApproachData;
};
